import axiosInstance from "../config/axios";
import API_ENDPOINTS from "../config/endpoints";

// Helper function to build full image URL from relative path
const buildImageUrl = (imagePath) => {
  if (!imagePath) return '';
  // If already a full URL, return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  // If relative path, prepend base URL if available
  const imageBase = import.meta.env.VITE_API_IMAGE_PATH || import.meta.env.VITE_API_BASE_URL || '';
  if (imageBase && !imagePath.startsWith('/')) {
    return `${imageBase}/${imagePath}`;
  } else if (imageBase) {
    return `${imageBase}${imagePath}`;
  }
  return imagePath;
};

// Helper function to transform API monthly donation to component format
const transformMonthlyDonation = (donation) => {
  // Get image - prefer thumbnail_image, fallback to first carousel image, then banner_image
  let imageUrl = '';
  if (donation.thumbnail_image) {
    imageUrl = donation.thumbnail_image;
  } else if (Array.isArray(donation.monthly_image_carousel) && donation.monthly_image_carousel.length > 0) {
    imageUrl = donation.monthly_image_carousel[0];
  } else if (donation.banner_image) {
    imageUrl = donation.banner_image;
  }

  return {
    id: donation.id,
    image: buildImageUrl(imageUrl),
    taxBenefit: !!donation.exemption_tag, // Show tax benefit if exemption_tag exists
    title: donation.title || '',
    peopleJoined:
      donation?.totalDonors ??
      donation?.Totaldonors ??
      donation?.total_donors ??
      donation?.people_joined ??
      0,
    // Include all original data for detail page
    originalData: donation,
  };
};

// Get Monthly Donations
// params: { category?: string, limit?: number }
export const getMonthlyDonations = async (params = {}, includeRawData = false) => {
  try {
    const response = await axiosInstance.get(API_ENDPOINTS.GET_MONTHLY_DONATIONS);
    let donations = [];
    if (Array.isArray(response)) {
      donations = response;
    } else if (response && Array.isArray(response.data)) {
      donations = response.data;
    }

    let filtered = donations.filter((donation) => donation.status === 1);

    // Category filter — match against categoryInfo.name if present
    if (params.category) {
      filtered = filtered.filter((donation) => {
        const catName = donation.categoryInfo?.name || "";
        return catName.toLowerCase() === String(params.category).toLowerCase();
      });
    }

    // Limit filter — sort newest-first before slicing
    if (params.limit) {
      filtered = filtered.sort((a, b) => {
        const dateA = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const dateB = new Date(b.updatedAt || b.createdAt || 0).getTime();
        return dateB - dateA;
      });
      filtered = filtered.slice(0, Number(params.limit));
    }

    return {
      data: filtered.map(transformMonthlyDonation),
      rawData: includeRawData ? filtered : undefined,
    };
  } catch (error) {
    throw error;
  }
};

// Get Monthly Donation by ID
export const getMonthlyDonationById = async (id) => {
  try {
    // Try to fetch by ID endpoint first
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.GET_MONTHLY_DONATION_BY_ID(id));
      // Axios interceptor returns response.data, so response is already the API response body
      // Handle both direct object and wrapped in data property
      let donation = null;
      if (response) {
        if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
          donation = response.data;
        } else if (typeof response === 'object' && !Array.isArray(response) && response.id) {
          donation = response;
        }
      }
      if (donation) {
        return { data: donation };
      }
    } catch (byIdError) {
      // If by-id endpoint doesn't exist or fails, fetch all and filter
      console.log('By-ID endpoint failed, fetching all donations:', byIdError);
    }
    
    // Fallback: Fetch all donations and find by ID
    const allResponse = await getMonthlyDonations(true); // Get raw data too
    if (allResponse?.rawData) {
      const found = allResponse.rawData.find(item => String(item.id) === String(id));
      if (found) {
        return { data: found };
      }
    }
    // Also check transformed data as backup
    if (allResponse?.data) {
      const found = allResponse.data.find(item => {
        const originalId = item.originalData?.id || item.id;
        return String(originalId) === String(id);
      });
      if (found?.originalData) {
        return { data: found.originalData };
      }
    }
    
    return { data: null };
  } catch (error) {
    throw error;
  }
};

